/**
 * Tâche 1443 (Eric 2026-10-02) — Page « Orthographe ».
 *
 * > « ajoute une page pour permettre aux concepteurs de cours d'une compagnie
 * >   (et les rôles supérieurs) d'indiquer des erreurs orthographiques
 * >   courantes, d'informer l'IA pour guider les corrections des
 * >   transcriptions, et leur éviter des erreurs récurrentes. »
 *
 * Portée confirmée par Eric : concepteur, admin et fondateur UNIQUEMENT. Le
 * contrôle réel est au SERVEUR (`require_designer`) ; la route protégée n'est
 * qu'un confort.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Info, MagnifyingGlass, Plus, SpinnerGap, TextAa,
} from "@phosphor-icons/react";
import { API_BASE as API } from "../lib/apiBase";
import { useLang } from "../contexts/LanguageContext";
import TermeCard from "../components/orthographe/TermeCard";
import ListeExclusions from "../components/orthographe/ListeExclusions";
import BalayageResultats from "../components/orthographe/BalayageResultats";
import HistoriqueBalayages from "../components/orthographe/HistoriqueBalayages";
import PropositionsIA from "../components/orthographe/PropositionsIA"; // Tâche 1445
import FiltresZones from "../components/orthographe/FiltresZones"; // Tâche 1458
import BandeauTermesAjoutes from "../components/orthographe/BandeauTermesAjoutes"; // Tâche 1455
import AccepterCategorie from "../components/orthographe/AccepterCategorie"; // Tâche 1457
import FautesFrequentes from "../components/orthographe/FautesFrequentes"; // Tâche 1484

const ONGLETS = [
  ["termes", "Termes et variantes", "Terms and variants"],
  ["detection", "Détection dans toute la base", "Detection across the database"],
  ["historique", "Historique des balayages", "Scan history"],
  // Tâche 1445 (Eric 2026-10-05) — L'IA propose d'elle-même les termes cibles.
  ["propositions", "Propositions de l'IA", "AI suggestions"],
  // Tâche 1484 (Eric) — Ce que les apprenants tapent vraiment.
  ["fautes", "Fautes fréquentes", "Frequent mistakes"],
];

const messageErreur = (err, repli) =>
  err?.response?.data?.detail || err?.message || repli;

export default function OrthographePage() {
  const { lang, tr } = useLang();
  const [onglet, setOnglet] = useState("termes");
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [nouveauTerme, setNouveauTerme] = useState("");
  const [nouvellesVariantes, setNouvellesVariantes] = useState("");
  const [creation, setCreation] = useState(false);

  const [balayage, setBalayage] = useState(null);
  const [libellesZone, setLibellesZone] = useState({});
  const [balayageEnCours, setBalayageEnCours] = useState(false);
  const [historique, setHistorique] = useState([]);
  const [historiqueChargement, setHistoriqueChargement] = useState(false);
  const [balayageOuvert, setBalayageOuvert] = useState(null);
  // Tâche 1455 — les termes ajoutés depuis le balayage affiché.
  const [termesAjoutes, setTermesAjoutes] = useState([]);
  // Tâche 1458 — catégories à montrer. Vide = toutes.
  const [zonesFiltre, setZonesFiltre] = useState([]);
  // Tâche 1454 — ordre d'affichage des termes cibles.
  const [tri, setTri] = useState("alpha_asc");

  // Tâche 1445 — propositions de l'IA, et ce qui a été écarté.
  const [propositions, setPropositions] = useState(null);
  const [propositionsEnCours, setPropositionsEnCours] = useState(false);
  const [propositionsEcartees, setPropositionsEcartees] = useState([]);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await axios.get(`${API}/api/orthographe/termes`,
        { withCredentials: true });
      setDonnees(data);
    } catch (err) {
      toast.error(messageErreur(err, tr("Chargement impossible.", "Could not load.")));
      setDonnees({ termes: [], exclusions: [], nb_variantes_actives: 0, nb_en_attente: 0 });
    } finally {
      setChargement(false);
    }
  }, [tr]);

  useEffect(() => { charger(); }, [charger]);

  const chargerHistorique = useCallback(async () => {
    setHistoriqueChargement(true);
    try {
      const { data } = await axios.get(`${API}/api/orthographe/balayages`,
        { withCredentials: true });
      setHistorique(data.balayages || []);
      setLibellesZone(data.libelles_zone || {});
    } catch (err) {
      toast.error(messageErreur(err, tr("Historique indisponible.", "History unavailable.")));
    } finally {
      setHistoriqueChargement(false);
    }
  }, [tr]);

  useEffect(() => {
    if (onglet === "historique" && !historique.length) chargerHistorique();
  }, [onglet, historique.length, chargerHistorique]);

  const chargerEcartees = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/orthographe/propositions/ecartees`,
        { withCredentials: true });
      setPropositionsEcartees(data.ecartees || []);
    } catch { /* une liste d'écartées absente ne bloque rien */ }
  }, []);

  useEffect(() => {
    if (onglet === "propositions") chargerEcartees();
  }, [onglet, chargerEcartees]);

  // Tâche 1484 — Les fautes réellement tapées par les apprenants.
  const [fautes, setFautes] = useState(null);
  const [fautesEnCours, setFautesEnCours] = useState(false);
  const chargerFautes = useCallback(async () => {
    setFautesEnCours(true);
    try {
      const { data } = await axios.get(
        `${API}/api/orthographe/fautes-frequentes?limite=50`,
        { withCredentials: true });
      setFautes(data);
    } catch (err) {
      toast.error(messageErreur(err, tr("Fautes fréquentes indisponibles.",
                                        "Frequent mistakes unavailable.")));
    } finally {
      setFautesEnCours(false);
    }
  }, []);
  useEffect(() => {
    if (onglet === "fautes") chargerFautes();
  }, [onglet, chargerFautes]);

  const ajouterFauteAuCatalogue = async (motSaisi, terme) => {
    try {
      const { data } = await axios.post(
        `${API}/api/orthographe/fautes-frequentes/au-catalogue`,
        { mot_saisi: motSaisi, terme },
        { withCredentials: true });
      toast.success(data.terme_cree
        ? tr(`« ${terme} » créé avec la faute « ${motSaisi} »`,
             `"${terme}" created with mistake "${motSaisi}"`)
        : tr(`« ${motSaisi} » ajoutée aux variantes de « ${terme} »`,
             `"${motSaisi}" added to variants of "${terme}"`));
      await Promise.all([chargerFautes(), charger()]);
    } catch (err) {
      toast.error(messageErreur(err, tr("Ajout impossible.", "Could not add.")));
    }
  };

  const lancerPropositions = async () => {
    setPropositionsEnCours(true);
    try {
      const { data } = await axios.post(`${API}/api/orthographe/propositions`, {},
        { withCredentials: true });
      setPropositions(data);
      if (!data.propositions?.length) {
        toast.info(data.motif || tr("Aucune faute récurrente retenue.",
                                    "No recurring mistake kept."));
      }
    } catch (err) {
      toast.error(messageErreur(err, tr("Recherche impossible.", "Search failed.")));
    } finally {
      setPropositionsEnCours(false);
    }
  };

  const accepterProposition = async (prop) => {
    try {
      await axios.post(`${API}/api/orthographe/propositions/accepter`, {
        cle: prop.cle, terme: prop.terme, faute: prop.faute,
      }, { withCredentials: true });
      await charger();
      return true;
    } catch (err) {
      toast.error(messageErreur(err, tr("Ajout impossible.", "Could not add.")));
      return false;
    }
  };

  const ecarterProposition = async (prop) => {
    try {
      await axios.post(`${API}/api/orthographe/propositions/ecarter`, {
        cle: prop.cle, terme: prop.terme, faute: prop.faute,
      }, { withCredentials: true });
      await chargerEcartees();
      return true;
    } catch (err) {
      toast.error(messageErreur(err, tr("Action impossible.", "Action failed.")));
      return false;
    }
  };

  const reconsiderer = async (cle) => {
    try {
      await axios.delete(
        `${API}/api/orthographe/propositions/ecartees/${encodeURIComponent(cle)}`,
        { withCredentials: true });
      await chargerEcartees();
    } catch (err) {
      toast.error(messageErreur(err, tr("Action impossible.", "Action failed.")));
    }
  };

  // ── Termes ────────────────────────────────────────────────────────────────
  const creer = async () => {
    const terme = nouveauTerme.trim();
    if (terme.length < 2) return;
    setCreation(true);
    try {
      await axios.post(`${API}/api/orthographe/termes`, {
        terme,
        variantes: nouvellesVariantes.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean),
      }, { withCredentials: true });
      setNouveauTerme("");
      setNouvellesVariantes("");
      await charger();
      toast.success(tr(`« ${terme} » ajouté.`, `"${terme}" added.`));
    } catch (err) {
      toast.error(messageErreur(err, tr("Ajout impossible.", "Could not add.")));
    } finally {
      setCreation(false);
    }
  };

  const ajouterVariantes = async (termeId, textes) => {
    try {
      const { data } = await axios.post(
        `${API}/api/orthographe/termes/${termeId}/variantes`,
        { textes }, { withCredentials: true });
      await charger();
      if (data.ignorees?.length) {
        toast.warning(tr(
          `Écartées (déjà connues ou identiques au terme cible) : ${data.ignorees.join(", ")}`,
          `Discarded (already known, or identical to the target term): ${data.ignorees.join(", ")}`));
      }
    } catch (err) {
      toast.error(messageErreur(err, tr("Ajout impossible.", "Could not add.")));
    }
  };

  const retirerVariante = async (termeId, texte) => {
    try {
      await axios.delete(`${API}/api/orthographe/termes/${termeId}/variantes`,
        { params: { texte }, withCredentials: true });
      await charger();
    } catch (err) {
      toast.error(messageErreur(err, tr("Retrait impossible.", "Could not remove.")));
    }
  };

  const changerStatut = async (termeId, texte, statut) => {
    try {
      await axios.post(`${API}/api/orthographe/termes/${termeId}/variantes/statut`,
        { texte, statut }, { withCredentials: true });
      await charger();
    } catch (err) {
      toast.error(messageErreur(err, tr("Action impossible.", "Action failed.")));
    }
  };

  const suggerer = async (termeId) => {
    try {
      const { data } = await axios.post(
        `${API}/api/orthographe/termes/${termeId}/suggerer`, {},
        { withCredentials: true });
      await charger();
      if (!data.proposees?.length) {
        toast.info(tr("L'IA n'a proposé aucune variante nouvelle.",
                      "The AI suggested no new variant."));
      } else {
        toast.success(tr(
          `${data.proposees.length} proposition(s) à valider — rien n'est appliqué avant votre accord.`,
          `${data.proposees.length} suggestion(s) awaiting your review — nothing is applied before you agree.`));
      }
    } catch (err) {
      toast.error(messageErreur(err, tr("Suggestion impossible.", "Suggestion failed.")));
    }
  };

  const renommer = async (termeId, terme) => {
    try {
      await axios.patch(`${API}/api/orthographe/termes/${termeId}`, { terme },
        { withCredentials: true });
      await charger();
    } catch (err) {
      toast.error(messageErreur(err, tr("Renommage impossible.", "Rename failed.")));
    }
  };

  const supprimer = async (termeId, terme) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(tr(
      `Supprimer « ${terme} » et toutes ses variantes ? Les corrections déjà appliquées ne sont pas annulées.`,
      `Delete "${terme}" and all its variants? Fixes already applied are not undone.`))) return;
    try {
      await axios.delete(`${API}/api/orthographe/termes/${termeId}`,
        { withCredentials: true });
      await charger();
    } catch (err) {
      toast.error(messageErreur(err, tr("Suppression impossible.", "Delete failed.")));
    }
  };

  // ── Exclusions ────────────────────────────────────────────────────────────
  const ajouterExclusion = async (terme) => {
    try {
      await axios.post(`${API}/api/orthographe/exclusions`, { terme },
        { withCredentials: true });
      await charger();
    } catch (err) {
      toast.error(messageErreur(err, tr("Exclusion impossible.", "Could not exclude.")));
    }
  };

  const retirerExclusion = async (id) => {
    try {
      await axios.delete(`${API}/api/orthographe/exclusions/${id}`,
        { withCredentials: true });
      await charger();
    } catch (err) {
      toast.error(messageErreur(err, tr("Retrait impossible.", "Could not remove.")));
    }
  };

  // ── Balayage ──────────────────────────────────────────────────────────────
  // Tâche 1455 — le balayage encore à trier est REPRIS au chargement de la
  // page : il ne vivait que dans l'état de l'écran, et un simple rechargement
  // faisait perdre tout le tri déjà fait.
  const chargerCourant = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/orthographe/balayage/courant`,
        { withCredentials: true });
      setLibellesZone(data.libelles_zone || {});
      if (data.balayage) {
        setBalayage(data.balayage);
        setBalayageOuvert(data.balayage.id);
        setTermesAjoutes(data.termes_ajoutes || []);
      }
    } catch { /* l'absence de balayage en cours n'est pas une erreur */ }
  }, []);

  useEffect(() => { chargerCourant(); }, [chargerCourant]);

  const lancerBalayage = async () => {
    setBalayageEnCours(true);
    try {
      const { data } = await axios.post(`${API}/api/orthographe/balayage`, {},
        { withCredentials: true });
      setLibellesZone(data.libelles_zone || {});
      setTermesAjoutes([]);
      setZonesFiltre([]);
      if (!data.balayage) {
        toast.info(data.motif || tr("Rien à chercher.", "Nothing to search for."));
        setBalayage(null);
      } else {
        setBalayage(data.balayage);
        setBalayageOuvert(data.balayage.id);
        setHistorique([]);
      }
    } catch (err) {
      toast.error(messageErreur(err, tr("Balayage impossible.", "Scan failed.")));
    } finally {
      setBalayageEnCours(false);
    }
  };

  /** Relit le balayage affiché pour récupérer l'état de tri à jour. */
  const rafraichirBalayage = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await axios.get(`${API}/api/orthographe/balayages/${id}`,
        { withCredentials: true });
      setBalayage(data.balayage);
    } catch { /* l'affichage reste sur ce qu'il avait */ }
  }, []);

  const poserResolution = async (occ, etat) => {
    if (!balayage?.id) return;
    try {
      const { data } = await axios.post(
        `${API}/api/orthographe/balayages/${balayage.id}/resolutions`,
        { occ_ids: [occ.occ_id], etat }, { withCredentials: true });
      setBalayage((b) => (b ? { ...b, resolutions: data.resolutions || {} } : b));
    } catch (err) {
      toast.error(messageErreur(err, tr("Action impossible.", "Action failed.")));
    }
  };

  /** Tâche 1457 — « Accepter ce terme partout » / « toute la catégorie ». */
  const accepterEnMasse = async (portee) => {
    if (!balayage?.id) return;
    try {
      const { data } = await axios.post(
        `${API}/api/orthographe/balayages/${balayage.id}/en-masse`,
        portee, { withCredentials: true });
      await rafraichirBalayage(balayage.id);
      if (data.applique) {
        toast.success(tr(`${data.applique} correction(s) appliquée(s).`,
                         `${data.applique} fix(es) applied.`));
      }
      if (data.echecs?.length) {
        // ⚠️ Un échec est DIT, avec son motif : le texte d'une occurrence a pu
        // changer depuis le balayage, et un silence laisserait croire que tout
        // est corrigé.
        toast.warning(tr(
          `${data.echecs.length} endroit(s) non corrigé(s) : ${data.echecs[0].motif}`,
          `${data.echecs.length} place(s) not fixed: ${data.echecs[0].motif}`));
      }
      if (!data.applique && !data.echecs?.length) {
        toast.info(tr("Rien à corriger ici.", "Nothing to fix here."));
      }
    } catch (err) {
      toast.error(messageErreur(err, tr("Correction en masse impossible.",
                                        "Bulk fix failed.")));
    }
  };

  const ouvrirBalayage = async (id) => {
    try {
      const { data } = await axios.get(`${API}/api/orthographe/balayages/${id}`,
        { withCredentials: true });
      setBalayage(data.balayage);
      setBalayageOuvert(id);
      setLibellesZone(data.libelles_zone || {});
      setOnglet("detection");
    } catch (err) {
      toast.error(messageErreur(err, tr("Ouverture impossible.", "Could not open.")));
    }
  };

  /** Corrige UNE occurrence, ou annule la correction si `correctionId` est donné. */
  const corriger = async (occ, correctionId) => {
    try {
      if (correctionId) {
        await axios.post(`${API}/api/orthographe/corrections/${correctionId}/restaurer`,
          {}, { withCredentials: true });
        // Tâche 1455 — annuler une correction remet l'occurrence « à trier » :
        // la laisser marquée « corrigée » mentirait sur l'état du texte.
        await poserResolution(occ, "aucun");
        return { statut: "annule" };
      }
      const { data } = await axios.post(`${API}/api/orthographe/corrections`, {
        coll: occ.coll, doc_id: occ.doc_id, chemin: occ.chemin,
        variante: occ.variante, terme: occ.terme, zone: occ.zone,
        balayage_id: balayage?.id, occ_id: occ.occ_id,
      }, { withCredentials: true });
      setBalayage((b) => (b
        ? {
          ...b,
          resolutions: {
            ...(b.resolutions || {}),
            [occ.occ_id]: { etat: "corrige", correction_id: data.correction?.id },
          },
        }
        : b));
      return {
        statut: "fait",
        correction_id: data.correction?.id,
        restant: data.restant_dans_ce_champ || 0,
      };
    } catch (err) {
      return {
        statut: "erreur",
        message: messageErreur(err, tr("Correction refusée.", "Fix refused.")),
      };
    }
  };

  const occurrencesParTerme = {};
  (balayage?.occurrences || []).forEach((occ) => {
    occurrencesParTerme[occ.terme] = (occurrencesParTerme[occ.terme] || 0) + 1;
  });

  // Tâche 1458 — les comptes PAR CATÉGORIE, limités à ce qui reste à trier :
  // proposer « Q&R (12) » quand les douze sont déjà traitées enverrait sur une
  // liste vide.
  const comptesZone = {};
  (balayage?.occurrences || []).forEach((occ) => {
    if ((balayage.resolutions || {})[occ.occ_id]) return;
    comptesZone[occ.zone] = (comptesZone[occ.zone] || 0) + 1;
  });

  // Tâche 1454 — ⚠️ Le tri alphabétique passe par `localeCompare` en français
  // avec `sensitivity: "base"` : sans lui, « Éducation » tomberait APRÈS
  // « Zèbre » (ordre des codes de caractères) et la liste paraîtrait cassée.
  // Les termes sans date d'ajout (les plus anciens, créés avant que le champ
  // existe) sont rangés en dernier au lieu de remonter en tête par accident.
  const termesTries = useMemo(() => {
    const liste = [...(donnees?.termes || [])];
    const alpha = (a, b) => (a.terme || "").localeCompare(
      b.terme || "", "fr", { sensitivity: "base", numeric: true });
    const parDate = (a, b) => (a.created_at || "").localeCompare(b.created_at || "");
    if (tri === "alpha_desc") return liste.sort((a, b) => alpha(b, a));
    if (tri === "date_asc") {
      return liste.sort((a, b) => (a.created_at ? 0 : 1) - (b.created_at ? 0 : 1)
        || parDate(a, b) || alpha(a, b));
    }
    if (tri === "date_desc") {
      return liste.sort((a, b) => (a.created_at ? 0 : 1) - (b.created_at ? 0 : 1)
        || parDate(b, a) || alpha(a, b));
    }
    return liste.sort(alpha);
  }, [donnees, tri]);

  return (
    <div className="max-w-5xl" data-testid="orthographe-page">
      <div className="flex items-start gap-3 mb-2">
        <TextAa size={26} className="text-[#002FA7] flex-shrink-0 mt-0.5" weight="duotone" />
        <div className="min-w-0">
          <h1 className="text-xl md:text-2xl font-semibold text-zinc-900">
            {tr("Orthographe", "Spelling")}
          </h1>
          <p className="text-sm text-zinc-600 mt-1">
            {tr("Indiquez la graphie correcte de vos termes métier et les fautes que vous voyez revenir. L'IA s'en sert pour corriger les prochaines transcriptions, et le balayage vous montre où ces fautes se trouvent déjà.",
                "Record the correct spelling of your business terms and the mistakes you keep seeing. The AI uses them to fix upcoming transcripts, and the scan shows you where those mistakes already are.")}
          </p>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 mb-5">
        <Info size={15} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-blue-900">
          {tr("Cette page est réservée aux concepteurs de cours, aux administrateurs et aux fondateurs. Le balayage DÉTECTE sans rien modifier : chaque correction se fait occurrence par occurrence, et reste annulable.",
              "This page is restricted to course designers, administrators and founders. The scan DETECTS without changing anything: each fix is applied one occurrence at a time, and stays undoable.")}
        </p>
      </div>

      <div className="flex gap-1 border-b border-zinc-200 mb-5 overflow-x-auto">
        {ONGLETS.map(([cle, fr, en]) => (
          <button
            key={cle}
            type="button"
            onClick={() => setOnglet(cle)}
            className={`px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
              onglet === cle
                ? "border-[#002FA7] text-[#002FA7]"
                : "border-transparent text-zinc-500 hover:text-zinc-800"
            }`}
            data-testid={`orthographe-onglet-${cle}`}
          >
            {lang === "en" ? en : fr}
            {cle === "termes" && donnees?.nb_en_attente > 0 && (
              <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">
                {donnees.nb_en_attente}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tâche 1485 (Eric) — Pendant une MISE À JOUR, la liste reste en
          place : le « Chargement… » plein écran démontait les cartes (le mode
          édition et les listes ouvertes se refermaient) et renvoyait la page
          en haut à chaque acceptation ou refus. Ici, un simple témoin. */}
      {chargement && donnees && (
        <div className="flex items-center gap-2 text-[11px] text-zinc-400 mb-2"
             data-testid="orthographe-maj">
          <SpinnerGap size={12} className="animate-spin" />
          {tr("Mise à jour…", "Updating…")}
        </div>
      )}

      {chargement && !donnees ? (
        <div className="flex items-center gap-2 text-sm text-zinc-500 py-10"
             data-testid="orthographe-chargement">
          <SpinnerGap size={16} className="animate-spin" />
          {tr("Chargement…", "Loading…")}
        </div>
      ) : onglet === "termes" ? (
        <div className="space-y-4">
          <div className="border border-zinc-200 bg-white rounded-xl p-4"
               data-testid="orthographe-creation">
            <h3 className="text-sm font-semibold text-zinc-900 mb-3">
              {tr("Ajouter un terme cible", "Add a target term")}
            </h3>
            <div className="grid md:grid-cols-[1fr_1.4fr_auto] gap-2">
              <input
                value={nouveauTerme}
                onChange={(e) => setNouveauTerme(e.target.value)}
                placeholder={tr("Graphie CORRECTE (ex. : CELIAPP)", "CORRECT spelling (e.g.: CELIAPP)")}
                className="px-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
                data-testid="orthographe-nouveau-terme"
              />
              <input
                value={nouvellesVariantes}
                onChange={(e) => setNouvellesVariantes(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") creer(); }}
                placeholder={tr("Variantes erronées, séparées par des virgules (facultatif)",
                                "Incorrect spellings, comma-separated (optional)")}
                className="px-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
                data-testid="orthographe-nouvelles-variantes"
              />
              <button
                type="button"
                onClick={creer}
                disabled={creation || nouveauTerme.trim().length < 2}
                className="inline-flex items-center justify-center gap-1 px-4 py-2 rounded-md bg-[#002FA7] text-white text-sm font-medium hover:bg-[#001f7a] transition-colors disabled:opacity-40"
                data-testid="orthographe-creer"
              >
                {creation ? <SpinnerGap size={14} className="animate-spin" /> : <Plus size={14} />}
                {tr("Ajouter", "Add")}
              </button>
            </div>
          </div>

          <ListeExclusions
            exclusions={donnees?.exclusions || []}
            onAjouter={ajouterExclusion}
            onRetirer={retirerExclusion}
          />

          {(donnees?.termes || []).length === 0 ? (
            <p className="text-sm text-zinc-400 py-6" data-testid="orthographe-aucun-terme">
              {tr("Aucun terme cible enregistré. Commencez par celui dont la faute revient le plus souvent.",
                  "No target term recorded. Start with the one whose misspelling comes back the most.")}
            </p>
          ) : (
            <>
              {/* Tâche 1454 (Eric 2026-06) — Tri de la liste des termes.
                  ⚠️ Le tri est fait DANS L'ÉCRAN, sans rappeler le serveur :
                  la liste est déjà entièrement chargée, et une requête par
                  changement de tri ferait clignoter la page pour rien. */}
              <div className="flex items-center justify-end gap-2">
                <label className="text-[11px] text-zinc-500" htmlFor="orthographe-tri">
                  {tr("Trier :", "Sort:")}
                </label>
                <select
                  id="orthographe-tri"
                  value={tri}
                  onChange={(e) => setTri(e.target.value)}
                  className="px-2 py-1.5 border border-zinc-300 rounded-md text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
                  data-testid="orthographe-tri"
                >
                  <option value="alpha_asc">{tr("A → Z", "A → Z")}</option>
                  <option value="alpha_desc">{tr("Z → A", "Z → A")}</option>
                  <option value="date_desc">{tr("Ajout : du plus récent", "Added: newest first")}</option>
                  <option value="date_asc">{tr("Ajout : du plus ancien", "Added: oldest first")}</option>
                </select>
              </div>
              {termesTries.map((t) => (
                <TermeCard
                  key={t.id}
                  terme={t}
                  occurrences={occurrencesParTerme[t.terme] ?? null}
                  onAjouter={ajouterVariantes}
                  onRetirer={retirerVariante}
                  onStatut={changerStatut}
                  onSuggerer={suggerer}
                  onRenommer={renommer}
                  onSupprimer={supprimer}
                />
              ))}
            </>
          )}
        </div>
      ) : onglet === "detection" ? (
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <button
              type="button"
              onClick={lancerBalayage}
              disabled={balayageEnCours || !donnees?.nb_variantes_actives}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#002FA7] text-white text-sm font-medium hover:bg-[#001f7a] transition-colors disabled:opacity-40"
              data-testid="orthographe-lancer-balayage"
            >
              {balayageEnCours
                ? <SpinnerGap size={16} className="animate-spin" />
                : <MagnifyingGlass size={16} />}
              {tr("Détecter ces fautes dans toute la base", "Detect these mistakes across the database")}
            </button>
            {/* Tâche 1457 — à DROITE du bouton de détection, comme demandé. */}
            {balayage && (
              <AccepterCategorie
                comptes={comptesZone}
                libellesZone={libellesZone}
                onAccepter={(zone) => accepterEnMasse({ zone })}
              />
            )}
            <span className="text-[11px] text-zinc-500">
              {donnees?.nb_variantes_actives
                ? tr(`${donnees.nb_variantes_actives} variante(s) active(s) seront recherchées. Transcriptions Whisper, Fireflies et fusionnée, tours de parole, synthèses, concepts, Q&R, procédures, objections, dictionnaire, titres, tags et scripts de vente.`,
                     `${donnees.nb_variantes_actives} active variant(s) will be searched: Whisper, Fireflies and merged transcripts, speaker turns, syntheses, concepts, Q&A, procedures, objections, dictionary, titles, tags and sales scripts.`)
                : tr("Ajoutez d'abord au moins une variante erronée active.",
                     "Add at least one active incorrect spelling first.")}
            </span>
          </div>
          {balayage
            ? (
              <>
                <BandeauTermesAjoutes termes={termesAjoutes} />
                <FiltresZones
                  comptes={comptesZone}
                  libellesZone={libellesZone}
                  selection={zonesFiltre}
                  onChanger={setZonesFiltre}
                />
                <BalayageResultats
                  balayage={balayage}
                  libellesZone={libellesZone}
                  zonesFiltre={zonesFiltre}
                  onCorriger={corriger}
                  onRefuser={(occ) => poserResolution(occ, "refuse")}
                  onReprendre={(occ) => poserResolution(occ, "aucun")}
                  onAccepterTerme={(variante) => accepterEnMasse({ variante })}
                />
              </>
            )
            : (
              <p className="text-sm text-zinc-400" data-testid="orthographe-detection-vide">
                {tr("Aucun balayage affiché. Lancez-en un, ou ouvrez-en un depuis l'historique.",
                    "No scan displayed. Run one, or open one from the history.")}
              </p>
            )}
        </div>
      ) : onglet === "historique" ? (
        <HistoriqueBalayages
          balayages={historique}
          libellesZone={libellesZone}
          chargement={historiqueChargement}
          onOuvrir={ouvrirBalayage}
          selectionne={balayageOuvert}
        />
      ) : onglet === "fautes" ? (
        <FautesFrequentes
          donnees={fautes}
          chargement={fautesEnCours}
          lang={lang}
          onAjouter={ajouterFauteAuCatalogue}
        />
      ) : (
        <PropositionsIA
          donnees={propositions}
          chargement={propositionsEnCours}
          ecartees={propositionsEcartees}
          onLancer={lancerPropositions}
          onAccepter={accepterProposition}
          onEcarter={ecarterProposition}
          onReconsiderer={reconsiderer}
        />
      )}
    </div>
  );
}
